import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Check, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/utils/cn';
import { getApiErrorMessage } from '@/utils/apiError';
import { AppearancePreview } from '../components/AppearancePreview';
import { PaletteCatalogPicker } from '../components/PaletteCatalogPicker';
import {
  conflictExtra,
  isConflict,
  useColorDropdown,
  useCreateAppearance,
  usePalette,
  usePatchAppearance,
} from '../api';
import type { AppearanceDetail, ColorDropdownItem, PaletteItem, PaletteKind } from '../types';
import {
  EMPTY_APPEARANCE_FORM,
  appearanceFieldErrors,
  appearancePatch,
  composeCodePreview,
  formValuesFromDetail,
} from '../utils/appearance';
import type { AppearanceFormValues } from '../utils/appearance';
import { FieldLabel } from './FieldLabel';

const schema = Yup.object({
  model_color: Yup.string().nullable().required('Vyberte barvu'),
  custom_title: Yup.string().max(255),
});

const INACTIVE_LABEL = { model_pattern: 'dezén', model_finish: 'úprava povrchu' } as const;

export function AppearanceFormDialog({
  modelId,
  initial,
  duplicate,
  onClose,
  onSaved,
  returnFocus,
}: {
  modelId: string;
  initial?: AppearanceDetail;
  duplicate?: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
  returnFocus: () => void;
}) {
  const editing = !!initial && !duplicate;
  const initialValues = useMemo<AppearanceFormValues>(() => {
    if (!initial) return EMPTY_APPEARANCE_FORM;
    const values = formValuesFromDetail(initial);
    return duplicate ? { ...values, custom_title: '' } : values;
  }, [initial, duplicate]);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [picker, setPicker] = useState<PaletteKind | null>(null);
  const colors = useColorDropdown(modelId);
  const patterns = usePalette('pattern', modelId);
  const finishes = usePalette('finish', modelId);
  const create = useCreateAppearance();
  const patch = usePatchAppearance();

  const formik = useFormik<AppearanceFormValues>({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      try {
        if (editing) {
          const input = appearancePatch(initialValues, values);
          if (isActive !== initial.is_active) input.is_active = isActive;
          if (!Object.keys(input).length) {
            onClose();
            return;
          }
          await patch.mutateAsync({ id: initial.id, input });
          toast.success('Vzhled byl uložen');
          onSaved(initial.id);
        } else {
          const created = await create.mutateAsync({
            model: modelId,
            custom_title: values.custom_title.trim() || null,
            model_color: values.model_color!,
            model_pattern: values.model_pattern,
            model_finish: values.model_finish,
          });
          toast.success(`Vzhled ${created.sys_title} byl vytvořen`);
          onSaved(created.id);
        }
      } catch (error) {
        if (isConflict(error)) {
          const inactive = conflictExtra(error).inactive ?? [];
          toast.error('Vzhled nelze aktivovat', {
            description: inactive.length
              ? `Vybraná položka palety (${inactive
                  .map((key) => INACTIVE_LABEL[key])
                  .join(', ')}) je odebraná. Obnovte ji v paletě, nebo ji ze vzhledu odeberte.`
              : getApiErrorMessage(error),
          });
          return;
        }
        const details = (error as any)?.response?.data?.details;
        helpers.setErrors(appearanceFieldErrors(details));
        toast.error(getApiErrorMessage(error));
      }
    },
  });
  const { values, errors, touched, setFieldValue } = formik;

  // Keep the currently assigned item selectable even if it was removed from the palette.
  const withCurrent = (items: PaletteItem[] = [], current?: PaletteItem | null) =>
    current && !items.some((item) => item.id === current.id) ? [...items, current] : items;
  const patternItems = withCurrent(patterns.data, initial?.model_pattern);
  const finishItems = withCurrent(finishes.data, initial?.model_finish);
  const colorOptions = useMemo(() => {
    const options: ColorChoiceOption[] = (colors.data ?? []).map((item) => ({
      ...item,
      inactive: false,
    }));
    const current = initial?.model_color;
    if (current && !options.some((item) => item.id === current.id)) {
      options.push({
        id: current.id,
        code: current.code,
        title: current.title,
        hex: current.hex,
        inactive: !current.is_active,
      });
    }
    return options;
  }, [colors.data, initial?.model_color]);
  const color = colorOptions.find((item) => item.id === values.model_color);
  const pattern = patternItems.find((item) => item.id === values.model_pattern);
  const finish = finishItems.find((item) => item.id === values.model_finish);
  const code = composeCodePreview(color?.code, pattern?.code, finish?.code);
  const saving = create.isLoading || patch.isLoading;
  let dialogTitle = 'Nový vzhled';
  if (editing) dialogTitle = 'Upravit vzhled';
  else if (duplicate) dialogTitle = 'Duplikovat vzhled';

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open && !picker) onClose();
        }}
      >
        <DialogContent
          className="sm:max-w-5xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            returnFocus();
          }}
        >
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              Vzhled tvoří barva, dezén a úprava povrchu. Barva je povinná a vybírá se z palety
              barev modelu.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="text-neutral-800">
            <form
              id="appearance-form"
              onSubmit={formik.handleSubmit}
              className="grid lg:grid-cols-[1fr_260px] gap-6"
            >
              <div className="space-y-6">
                <ColorChoice
                  options={colorOptions}
                  loading={colors.isLoading}
                  selectedId={values.model_color}
                  error={touched.model_color ? errors.model_color : undefined}
                  onSelect={(id) => setFieldValue('model_color', id)}
                />
                <PaletteChoice
                  title="Dezén"
                  noneLabel="Žádný"
                  items={patternItems}
                  loading={patterns.isLoading}
                  selectedId={values.model_pattern}
                  error={errors.model_pattern}
                  onSelect={(id) => setFieldValue('model_pattern', id)}
                  onAdd={() => setPicker('pattern')}
                />
                <PaletteChoice
                  title="Úprava povrchu"
                  noneLabel="Žádná"
                  items={finishItems}
                  loading={finishes.isLoading}
                  selectedId={values.model_finish}
                  error={errors.model_finish}
                  onSelect={(id) => setFieldValue('model_finish', id)}
                  onAdd={() => setPicker('finish')}
                />
              </div>
              <aside className="lg:border-l border-neutral-150 lg:pl-5 space-y-4">
                <h3 className="text-sm font-semibold">Náhled</h3>
                <AppearancePreview
                  hex={color?.hex ?? null}
                  patternImageUrl={pattern?.classified_solution.image_url}
                  finishImageUrl={finish?.classified_solution.image_url}
                  label="Náhled vzhledu"
                  className="h-40"
                />
                <div>
                  <p className="text-[10px] uppercase text-neutral-500">Kód</p>
                  <p className="font-mono text-xs mt-1" aria-live="polite">
                    {code}
                  </p>
                </div>
                <FieldLabel label="Vlastní název" error={errors.custom_title}>
                  <Input
                    name="custom_title"
                    value={values.custom_title}
                    maxLength={255}
                    onChange={formik.handleChange}
                    placeholder={`Bez názvu se zobrazí ${code}`}
                    data-testid="appearance-custom-title-input"
                  />
                </FieldLabel>
                {editing && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={isActive}
                      onCheckedChange={(checked) => setIsActive(!!checked)}
                    />
                    Aktivní
                  </label>
                )}
              </aside>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="appearance-form-cancel-btn">
              Storno
            </Button>
            <Button
              type="submit"
              form="appearance-form"
              disabled={saving}
              data-testid="appearance-form-submit-btn"
            >
              {editing ? 'Uložit změny' : 'Vytvořit vzhled'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {picker && (
        <PaletteCatalogPicker
          kind={picker}
          modelId={modelId}
          onClose={() => setPicker(null)}
          onAdded={(item) => {
            setFieldValue(picker === 'pattern' ? 'model_pattern' : 'model_finish', item.id);
            setPicker(null);
          }}
        />
      )}
    </>
  );
}

interface ColorChoiceOption extends ColorDropdownItem {
  inactive: boolean;
}

function ColorChoice({
  options,
  loading,
  selectedId,
  error,
  onSelect,
}: {
  options: ColorChoiceOption[];
  loading: boolean;
  selectedId: string | null;
  error?: string;
  onSelect: (id: string) => void;
}) {
  return (
    <section aria-labelledby="appearance-color">
      <h3 id="appearance-color" className="text-sm font-semibold mb-2">
        Barva
      </h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {options.map((option) => {
          const selected = selectedId === option.id;
          return (
            <button
              type="button"
              key={option.id}
              aria-label={`${option.code} ${option.title}`}
              aria-pressed={selected}
              onClick={() => onSelect(option.id)}
              className={cn(
                'rounded border p-2 text-left text-xs transition-colors',
                selected
                  ? 'border-primary-500 bg-primary-50 text-primary-800'
                  : 'border-neutral-200 hover:border-neutral-400'
              )}
            >
              <AppearancePreview hex={option.hex} label={option.title} className="h-14 w-full" />
              <span className="mt-2 flex items-start gap-1">
                <span className="min-w-0 flex-1 break-words">
                  <span className="font-mono text-neutral-500">{option.code} </span>
                  {option.title}
                  {option.inactive && (
                    <span className="block text-amber-700">odebráno z palety</span>
                  )}
                </span>
                {selected && <Check size={12} className="shrink-0 mt-0.5" />}
              </span>
            </button>
          );
        })}
      </div>
      {!loading && !options.length && (
        <p className="text-xs text-neutral-500 mt-2">
          Paleta barev je prázdná. Nejdřív založte barvu v knihovně Barvy.
        </p>
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </section>
  );
}

function PaletteChoice({
  title,
  noneLabel,
  items,
  loading,
  selectedId,
  error,
  onSelect,
  onAdd,
}: {
  title: string;
  noneLabel: string;
  items: PaletteItem[];
  loading: boolean;
  selectedId: string | null;
  error?: string;
  onSelect: (id: string | null) => void;
  onAdd: () => void;
}) {
  const options = [{ id: null, code: '', title: noneLabel, image: null, inactive: false }].concat(
    items.map((item) => ({
      id: item.id,
      code: item.code,
      title: item.title,
      image: item.classified_solution.image_url,
      inactive: !item.is_active,
    }))
  );
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Button type="button" variant="link" size="sm" onClick={onAdd}>
          <Plus />
          Přidat z katalogu
        </Button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {options.map((option) => {
          const selected = selectedId === option.id;
          return (
            <button
              type="button"
              key={option.id ?? 'none'}
              aria-label={option.code ? `${option.code} ${option.title}` : option.title}
              aria-pressed={selected}
              onClick={() => onSelect(option.id)}
              className={cn(
                'rounded border p-2 text-left text-xs transition-colors',
                selected
                  ? 'border-primary-500 bg-primary-50 text-primary-800'
                  : 'border-neutral-200 hover:border-neutral-400'
              )}
            >
              <AppearancePreview
                patternImageUrl={option.image}
                label={option.title}
                className="h-14 w-full"
              />
              <span className="mt-2 flex items-start gap-1">
                <span className="min-w-0 flex-1 break-words">
                  {option.code && (
                    <span className="font-mono text-neutral-500">{option.code} </span>
                  )}
                  {option.title}
                  {option.inactive && (
                    <span className="block text-amber-700">odebráno z palety</span>
                  )}
                </span>
                {selected && <Check size={12} className="shrink-0 mt-0.5" />}
              </span>
            </button>
          );
        })}
      </div>
      {!loading && !items.length && (
        <p className="text-xs text-neutral-500 mt-2">
          Paleta modelu je prázdná. Přidejte položku z katalogu.
        </p>
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </section>
  );
}
